<template>
  <div class="data-table">
    <!-- Table Actions -->
    <div class="table-actions">
      <div class="table-info">
        <span class="info-text">
          从 <span class="info-number">{{ formatNumber(dbTotalRows) }}</span> 条记录中检索到 <span class="info-number">{{ computedTotal.toLocaleString() }}</span> 条数据
        </span>
      </div>
      <div class="table-buttons">
        <a-button
          size="small"
          class="custom-column-btn"
          :loading="exportLoading"
          @click="handleExportClick"
        >
          <Download :size="16" class="btn-icon" />
          导出
        </a-button>
        <a-button
          size="small"
          class="custom-column-btn"
          @click="showColumnDialog = true"
        >
          <Settings :size="16" class="btn-icon" />
          自定义列
        </a-button>
      </div>
    </div>

    <!-- Table -->
    <div ref="tableWrapper" class="table-wrapper">
      <a-table
        :columns="tableColumns"
        :data-source="currentData"
        :pagination="false"
        :scroll="{ x: tableScrollWidth, y: tableScrollHeight }"
        :loading="loading"
        class="data-table-content"
        :class="{ 'is-col-resizing': !!resizeState }"
        @row="handleTableRow"
      >
        <template #headerCell="{ column }">
          <div v-if="column.key !== 'actions'" class="th-resize-wrap">
            <span class="th-title">{{ column.title }}</span>
            <span
              class="th-resize-handle"
              title="拖动调整列宽"
              @mousedown.prevent.stop="startColumnResize($event, column.key)"
            />
          </div>
          <span v-else>{{ column.title }}</span>
        </template>
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'actions'">
            <div @click.stop>
              <slot name="actions" :record="record" />
            </div>
          </template>
          <template v-else-if="shouldRenderAsTag(column.key)">
            <a-tooltip v-if="record[column.key]" v-bind="getCellTooltipProps(record[column.key])">
              <div class="tag-cell" :style="getCellMaxWidthStyle(column.key)">
                <a-tag
                  v-for="(tag, index) in getTagList(record[column.key])"
                  :key="index"
                  class="custom-tag"
                >
                  {{ tag }}
                </a-tag>
              </div>
            </a-tooltip>
            <span v-else>-</span>
          </template>
          <template v-else-if="isListType(column.key)">
            <a-tooltip v-bind="getCellTooltipProps(record[column.key])">
              <div :style="getCellMaxWidthStyle(column.key)">
                <template v-if="getListItems(record[column.key]).length > 0">
                  <span v-for="(item, index) in getListItems(record[column.key])" :key="index">
                    {{ item }}<span v-if="index < getListItems(record[column.key]).length - 1">, </span>
                  </span>
                </template>
                <span v-else>-</span>
              </div>
            </a-tooltip>
          </template>
          <template v-else-if="column.key === 'description' || column.key === 'summary'">
            <a-tooltip v-bind="getCellTooltipProps(record[column.key])">
              <div class="description-cell" :style="getCellMaxWidthStyle(column.key)">
                {{ record[column.key] || '-' }}
              </div>
            </a-tooltip>
          </template>
          <template v-else-if="column.key === 'resource_link' || column.key === 'source_link' || column.key === 'link'">
            <a-tooltip v-if="record[column.key]" v-bind="getCellTooltipProps(record[column.key])">
              <a
                :href="record[column.key]"
                target="_blank"
                rel="noopener noreferrer"
                class="link-cell"
                :style="getCellMaxWidthStyle(column.key)"
                @click.stop
              >
                {{ record[column.key] }}
              </a>
            </a-tooltip>
            <span v-else>-</span>
          </template>
          <template v-else>
            <a-tooltip v-bind="getCellTooltipProps(getCellDisplayValue(column.key, record))">
              <div :style="getCellMaxWidthStyle(column.key)">
                {{ getCellDisplayValue(column.key, record) || '-' }}
              </div>
            </a-tooltip>
          </template>
        </template>
      </a-table>
    </div>

    <!-- Pagination -->
    <div class="table-pagination">
      <div class="pagination-left">
        <div class="page-size-selector">
          <span class="page-size-text">每页</span>
          <a-select
            :value="computedPageSize"
            size="small"
            class="page-size-select"
            @change="handlePageSizeChange"
          >
            <a-select-option :value="10">10</a-select-option>
            <a-select-option :value="20">20</a-select-option>
            <a-select-option :value="50">50</a-select-option>
            <a-select-option :value="100">100</a-select-option>
          </a-select>
          <span class="page-size-text">条</span>
        </div>
        <span class="total-text">
          共 {{ computedTotal.toLocaleString() }} 条
        </span>
      </div>
      <a-pagination
        :current="computedCurrentPage"
        :total="computedTotal"
        :page-size="computedPageSize"
        :show-size-changer="false"
        :show-quick-jumper="false"
        class="pagination-component"
        @change="handlePageChange"
      />
    </div>

    <!-- Custom Column Dialog -->
    <a-modal
      :open="showColumnDialog"
      title="自定义列显示"
      :footer="null"
      width="400px"
      class="column-dialog"
      @cancel="showColumnDialog = false"
    >
      <div class="column-list">
        <div
          v-for="column in localColumns"
          :key="column.key"
          class="column-item"
        >
          <a-checkbox
            :checked="column.visible"
            @change="handleColumnToggle(column.key)"
          >
            {{ column.label }}
          </a-checkbox>
        </div>
      </div>
      <div class="dialog-footer">
        <a-button type="primary" @click="showColumnDialog = false">确定</a-button>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { Settings, Download } from 'lucide-vue-next'
import { formatCreateTime } from '@/utils/formatCreateTime'
import {
  getStoredColumnWidths,
  saveStoredColumnWidths,
} from '@/utils/columnWidthStorage'

const DEFAULT_COL_WIDTH = 160
const MIN_COL_WIDTH = 60
const MAX_COL_WIDTH = 1200

export default {
  name: 'DataTable',
  components: {
    Settings,
    Download,
  },
  props: {
    columns: {
      type: Array,
      required: true,
      default: () => []
    },
    data: {
      type: Array,
      required: true,
      default: () => []
    },
    total: {
      type: Number,
      default: null
    },
    dbTotalRows: {
      type: Number,
      default: null
    },
    currentPage: {
      type: Number,
      default: null
    },
    pageSize: {
      type: Number,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    exportLoading: {
      type: Boolean,
      default: false,
    },
    labelGroup: {
      type: Number,
      default: null
    },
    onRowClick: {
      type: Function,
      default: null
    }
  },
  emits: ['row-click', 'page-change', 'page-size-change', 'export'],
  data() {
    return {
      localCurrentPage: 1,
      localPageSize: 10,
      showColumnDialog: false,
      localColumns: [],
      columnWidths: {},
      resizeState: null,
      tableScrollHeight: null,
      tableMaxHeight: null,
      resizeObserver: null,
    }
  },
  mounted() {
    // 使用 ResizeObserver 监听父容器大小变化
    this.$nextTick(() => {
      this.calculateTableHeight()
      const parentElement = this.$refs.tableWrapper?.parentElement
      if (parentElement && window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(() => {
          this.calculateTableHeight()
        })
        this.resizeObserver.observe(parentElement)
      } else {
        // 降级方案：使用 window resize 事件
        window.addEventListener('resize', this.calculateTableHeight)
      }
    })
  },
  beforeUnmount() {
    this.stopColumnResize()
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
    window.removeEventListener('resize', this.calculateTableHeight)
  },
  computed: {
    computedTotal() {
      return this.total !== null ? this.total : this.data.length
    },
    computedCurrentPage() {
      return this.currentPage !== null ? this.currentPage : this.localCurrentPage
    },
    computedPageSize() {
      return this.pageSize !== null ? this.pageSize : this.localPageSize
    },
    visibleColumns() {
      return this.localColumns.filter(col => col.visible)
    },
    tableColumns() {
      const cols = this.visibleColumns.map((col) => {
        const width = this.getEffectiveWidth(col)
        return {
          title: col.label,
          dataIndex: col.key,
          key: col.key,
          ellipsis: true,
          width,
          customCell: () => ({
            style: {
              maxWidth: `${width}px`,
              minWidth: `${Math.min(MIN_COL_WIDTH, width)}px`,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
          }),
          customHeaderCell: () => ({
            style: {
              maxWidth: `${width}px`,
              minWidth: `${Math.min(MIN_COL_WIDTH, width)}px`,
              position: 'relative',
              overflow: 'visible',
            },
          }),
        }
      })
      // 如果有 actions slot，添加操作列
      if (this.$slots.actions) {
        cols.push({
          title: '操作',
          key: 'actions',
          width: 120,
          fixed: 'right',
        })
      }
      return cols
    },
    // 计算表格总宽度，用于设置 scroll.x
    tableScrollWidth() {
      let totalWidth = 0
      this.visibleColumns.forEach((col) => {
        totalWidth += this.getEffectiveWidth(col)
      })
      if (this.$slots.actions) {
        totalWidth += 120
      }
      return Math.max(totalWidth, 800)
    },
    currentData() {
      // 如果传入了 total，说明是服务端分页，直接返回所有数据
      // 否则进行前端分页
      if (this.total !== null) {
        return this.data
      }
      const startIndex = (this.computedCurrentPage - 1) * this.computedPageSize
      const endIndex = startIndex + this.computedPageSize
      return this.data.slice(startIndex, endIndex)
    }
  },
  watch: {
    columns: {
      immediate: true,
      deep: true,
      handler(newColumns) {
        this.localColumns = newColumns.map((col) => ({ ...col }))
        this.loadColumnWidths()
      },
    },
    labelGroup() {
      this.loadColumnWidths()
    },
  },
  methods: {
    loadColumnWidths() {
      this.columnWidths = { ...getStoredColumnWidths(this.labelGroup) }
    },
    getEffectiveWidth(col) {
      if (!col) return DEFAULT_COL_WIDTH
      let next = DEFAULT_COL_WIDTH
      const saved = this.columnWidths[col.key]
      if (Number.isFinite(saved) && saved > 0) {
        next = saved
      } else if (col.width !== undefined && col.width !== null) {
        const n = Number(col.width)
        if (Number.isFinite(n) && n > 0) next = n
      } else if (col.maxWidth !== undefined && col.maxWidth !== null) {
        const n = Number(col.maxWidth)
        if (Number.isFinite(n) && n > 0) next = n
      }
      return Math.max(MIN_COL_WIDTH, Math.min(MAX_COL_WIDTH, next))
    },
    startColumnResize(event, columnKey) {
      if (!columnKey || columnKey === 'actions') return
      const col = this.localColumns.find((c) => c.key === columnKey)
      if (!col) return

      this.stopColumnResize()
      this.resizeState = {
        key: columnKey,
        startX: event.clientX,
        startWidth: this.getEffectiveWidth(col),
      }
      document.body.classList.add('pharmaid-col-resizing')
      document.addEventListener('mousemove', this.onColumnResizeMove)
      document.addEventListener('mouseup', this.onColumnResizeEnd)
    },
    onColumnResizeMove(event) {
      if (!this.resizeState) return
      const delta = event.clientX - this.resizeState.startX
      let next = this.resizeState.startWidth + delta
      next = Math.max(MIN_COL_WIDTH, Math.min(MAX_COL_WIDTH, next))
      this.columnWidths = {
        ...this.columnWidths,
        [this.resizeState.key]: next,
      }
    },
    onColumnResizeEnd() {
      if (this.resizeState?.key) {
        const key = this.resizeState.key
        const width = this.columnWidths[key]
        if (Number.isFinite(width) && width > 0) {
          saveStoredColumnWidths(this.labelGroup, { [key]: width })
        }
      }
      this.stopColumnResize()
    },
    stopColumnResize() {
      document.removeEventListener('mousemove', this.onColumnResizeMove)
      document.removeEventListener('mouseup', this.onColumnResizeEnd)
      document.body.classList.remove('pharmaid-col-resizing')
      this.resizeState = null
    },
    handlePageChange(page, pageSize) {
      if (this.currentPage === null) {
        this.localCurrentPage = page
      }
      if (pageSize && this.pageSize === null) {
        this.localPageSize = pageSize
      }
      this.$emit('page-change', page, pageSize)
    },
    handlePageSizeChange(size) {
      if (this.pageSize === null) {
        this.localPageSize = size
        this.localCurrentPage = 1
      }
      this.$emit('page-size-change', size)
    },
    handleExportClick() {
      const columns = this.visibleColumns.map((col) => ({
        key: col.key,
        title: col.label,
      }))
      this.$emit('export', columns)
    },
    handleColumnToggle(key) {
      const column = this.localColumns.find(col => col.key === key)
      if (column) {
        column.visible = !column.visible
      }
    },
    handleTableRow(record) {
      return {
        onClick: () => {
          if (this.onRowClick) {
            this.onRowClick(record)
          }
          this.$emit('row-click', record)
        }
      }
    },
    // 判断字段是否应该渲染为标签
    // 只有前沿技术的技术领域标签和疾病领域标签用tag处理
    shouldRenderAsTag(columnKey) {
      return columnKey === 'tech_areas_tag' || columnKey === 'disease_states_tag'
    },
    // 判断字段类型是否为 list
    isListType(columnKey) {
      const column = this.localColumns.find(col => col.key === columnKey)
      return column && column.type === 'list'
    },
    // 获取列表类型的所有元素数组
    getListItems(value) {
      if (!value) {
        return []
      }
      // 如果是数组，直接返回
      if (Array.isArray(value)) {
        return value.map(item => String(item)).filter(item => item)
      }
      // 如果是字符串，尝试按逗号或分号分割
      if (typeof value === 'string') {
        return value.split(/[,;]/).map(item => item.trim()).filter(item => item)
      }
      return [String(value)]
    },
    // 将"、"分隔的字符串或数组转换为标签数组
    getTagList(value) {
      if (!value) {
        return []
      }
      // 如果是数组，直接返回（过滤空值）
      if (Array.isArray(value)) {
        return value.filter(tag => tag && String(tag).trim()).map(tag => String(tag).trim())
      }
      // 如果是字符串，按"、"分隔
      if (typeof value === 'string') {
        return value.split('、').filter(tag => tag.trim()).map(tag => tag.trim())
      }
      return []
    },
    // 获取单元格宽度限制样式（跟随当前可调列宽）
    getCellMaxWidthStyle(columnKey) {
      const column = this.localColumns.find((col) => col.key === columnKey)
      if (!column) return {}
      const width = this.getEffectiveWidth(column)
      return {
        maxWidth: `${width}px`,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        display: 'block',
      }
    },
    /** 将单元格值格式化为可阅读的完整文本 */
    formatCellFullText(value) {
      if (value === null || value === undefined || value === '') {
        return ''
      }
      if (Array.isArray(value)) {
        return value
          .map((item) => this.formatCellItemText(item))
          .filter(Boolean)
          .join('、')
      }
      return this.formatCellItemText(value)
    },
    /** 单个元素：字符串直接用；对象优先取常见名称字段 */
    formatCellItemText(item) {
      if (item === null || item === undefined || item === '') {
        return ''
      }
      if (typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean') {
        return String(item).trim()
      }
      if (typeof item === 'object') {
        const named = item.name || item.device_name || item.title || item.label
        if (named) return String(named).trim()
        try {
          return JSON.stringify(item)
        } catch {
          return String(item)
        }
      }
      return String(item).trim()
    },
    /** 单元格展示值；创建/更新时间格式化到分 */
    getCellDisplayValue(columnKey, record) {
      if (columnKey === 'create_time' || columnKey === 'update_time') {
        return formatCreateTime(record?.[columnKey])
      }
      return this.formatCellFullText(record?.[columnKey])
    },
    /**
     * Tooltip 配置：有内容就挂 tip（避免同列有的有、有的没有）；
     * destroyTooltipOnHide 关闭后销毁浮层 DOM。
     */
    getCellTooltipProps(value) {
      const text = this.formatCellFullText(value)
      if (!text) {
        return { title: null }
      }
      return {
        title: text,
        placement: 'topLeft',
        mouseEnterDelay: 0.25,
        destroyTooltipOnHide: true,
        overlayStyle: { maxWidth: '480px' },
        overlayInnerStyle: {
          maxHeight: '280px',
          overflow: 'auto',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        },
      }
    },
    // 格式化数字显示（支持数字和字符串）
    formatNumber(value) {
      if (value === null || value === undefined) {
        return ''
      }
      // 如果是数字，使用 toLocaleString 格式化
      if (typeof value === 'number') {
        return value.toLocaleString()
      }
      // 如果是字符串，尝试转换为数字后格式化，如果转换失败则直接返回字符串
      const num = Number(value)
      if (!isNaN(num)) {
        return num.toLocaleString()
      }
      return String(value)
    },
    calculateTableHeight() {
      // 计算表格最大高度，考虑顶部导航栏（64px）
      this.$nextTick(() => {
        const viewportHeight = window.innerHeight
        const viewportWidth = window.innerWidth
        
        // 获取表格操作栏和分页的高度
        const tableActions = this.$el?.querySelector('.table-actions')
        const tablePagination = this.$el?.querySelector('.table-pagination')
        
        let actionsHeight = 0
        let paginationHeight = 0
        
        if (tableActions) {
          actionsHeight = tableActions.offsetHeight
        }
        if (tablePagination) {
          paginationHeight = tablePagination.offsetHeight
        }
        
        // 计算最大高度 = 视口高度 - 顶部导航(64px) - 操作栏 - 分页 - 边距
        // 边距：主内容区 padding + 筛选区域 margin
        let margin = 80 // 默认边距（主内容区 padding + 筛选区域 margin）
        
        // 1366*768 屏幕
        if (viewportHeight <= 768 && viewportWidth <= 1366) {
          margin = 60
        } 
        // Pad 设备
        else if (viewportWidth <= 1024) {
          margin = 50
        }
        
        // 表格最大高度
        const maxHeight = viewportHeight - 64 - actionsHeight - paginationHeight - margin
        this.tableMaxHeight = `${maxHeight}px`
        
        // scroll.y 使用相同的值（用于 Ant Design Table 的滚动）
        this.tableScrollHeight = maxHeight
        
        // 确保最小高度
        if (this.tableScrollHeight < 200) {
          this.tableScrollHeight = 200
          this.tableMaxHeight = '200px'
        }
      })
    }
  }
}
</script>

<style scoped>
.data-table {
  background: #fff;
  border-radius: 4px;
  border: 1px solid #d9d9d9;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
}

.table-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-bottom: 1px solid #f0f0f0;
}

.table-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-text {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.65);
}

.info-number {
  color: #1890ff;
}

.table-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.custom-column-btn {
  height: 32px;
  font-size: 14px;
  border: 1px solid #d9d9d9;
  color: rgba(0, 0, 0, 0.85);
  background: #fff;
  display: flex;
  align-items: center;
  gap: 4px;
}

.custom-column-btn:hover {
  color: #1890ff;
  border-color: #1890ff;
  background: #e6f7ff;
}

.btn-icon {
  margin-right: 4px;
}

.table-wrapper {
  position: relative;
  /* 不设置 overflow，让 Ant Design Table 自己处理滚动 */
}

.data-table-content {
  min-width: 100%;
}

.data-table-content :deep(.ant-table-thead > tr) {
  background: transparent;
}

.data-table-content :deep(.ant-table-thead > tr:hover) {
  background: transparent;
}

.data-table-content :deep(.ant-table-thead > tr > th) {
  background: #f0f5ff !important;
  color: rgba(0, 0, 0, 0.85);
  font-weight: 500;
  height: 48px;
  padding: 0 16px;
  position: relative;
  /* 最小宽度由列配置 / customHeaderCell 控制，避免压过 maxWidth */
  vertical-align: middle !important;
}

/* Ant Design Table 使用 scroll.y 时，表头会自动固定 */
/* 
 * 原因说明：
 * 1. macOS 系统默认行为：滚动条只在滚动时显示，停止后自动隐藏
 * 2. Ant Design Table 的滚动容器是 .ant-table-body，当设置了 scroll.x 时，会在该容器上启用横向滚动
 * 3. 要强制显示滚动条，需要：
 *    - 使用 overflow-x: scroll（而不是 auto）强制显示
 *    - 自定义滚动条样式，使其始终可见
 */
.data-table-content :deep(.ant-table-container) {
  /* 让 Ant Design 自己处理滚动条 */
}

/* 强制列宽固定，防止被内容撑开 */
/* 注意：table-layout: fixed 会阻止 maxWidth 生效，所以只在没有 maxWidth 配置时使用 */
.data-table-content :deep(.ant-table) {
  /* 移除 table-layout: fixed，以允许 maxWidth 生效 */
}

.data-table-content :deep(.ant-table-thead > tr > th),
.data-table-content :deep(.ant-table-tbody > tr > td) {
  word-wrap: break-word;
  word-break: break-all;
}


/* 强制显示横向滚动条（始终可见）
 * 
 * 原因说明：
 * 1. macOS 系统默认行为：滚动条只在滚动时显示，停止后自动隐藏（系统设置控制）
 *    这是 macOS 的系统级设置，在"系统设置 > 外观 > 显示滚动条"中可以设置为"始终"
 * 2. Ant Design Table 设置了 scroll.x: 'max-content' 后，横向滚动由 .ant-table-body 处理
 * 3. 要强制显示滚动条，需要：
 *    - 使用 overflow-x: scroll（而不是 auto）强制显示
 *    - 使用 -webkit-appearance: none 覆盖系统默认样式
 *    - 自定义滚动条样式，使其始终可见
 */
.data-table-content :deep(.ant-table-body) {
  overflow-x: scroll !important;
}

/* 自定义横向滚动条样式，覆盖 macOS 默认行为，使其始终可见 */
.data-table-content :deep(.ant-table-body)::-webkit-scrollbar {
  -webkit-appearance: none !important;
  height: 8px !important;
  display: block !important;
}

.data-table-content :deep(.ant-table-body)::-webkit-scrollbar-thumb {
  background: #bfbfbf !important;
  border-radius: 4px;
  -webkit-appearance: none !important;
}

.data-table-content :deep(.ant-table-body)::-webkit-scrollbar-thumb:hover {
  background: #999 !important;
}

.data-table-content :deep(.ant-table-body)::-webkit-scrollbar-track {
  background: #f0f0f0 !important;
  border-radius: 4px;
  -webkit-appearance: none !important;
}

/* 对于 Firefox - 强制显示滚动条 */
.data-table-content :deep(.ant-table-body) {
  scrollbar-width: thin !important;
  scrollbar-color: #bfbfbf #f0f0f0 !important;
}

.data-table-content :deep(.ant-table-header) {
  background: #f0f5ff;
  position: sticky;
  top: 0;
  z-index: 10;
}

.data-table-content :deep(.ant-table-thead) {
  position: sticky;
  top: 0;
  z-index: 10;
}

.data-table-content :deep(.ant-table-thead > tr > th) {
  background: #f0f5ff !important;
  position: sticky;
  top: 0;
  z-index: 10;
  /* 设置表头垂直对齐为居中 */
  vertical-align: middle !important;
}


.data-table-content :deep(.ant-table-tbody > tr) {
  cursor: pointer;
}

.data-table-content :deep(.ant-table-tbody > tr:hover) {
  background: #fafafa;
}

.data-table-content :deep(.ant-table-tbody > tr > td) {
  color: rgba(0, 0, 0, 0.85);
  padding: 16px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  /* 与表头一致，避免链接等 inline-block 内容基线对齐导致视觉上不居中 */
  vertical-align: middle;
  /* 最小宽度由列配置 / customCell 控制，避免压过 maxWidth */
}

.table-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-top: 1px solid #f0f0f0;
}

/* 1366*768 和 pad 响应式优化 */
@media (max-width: 1366px) {
  .table-pagination {
    padding: 12px 20px;
    flex-wrap: wrap;
    gap: 8px;
  }
  
  .table-actions {
    padding: 12px 20px;
  }
}

@media (max-width: 1024px) {
  .table-pagination {
    padding: 10px 16px;
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
  
  .pagination-left {
    justify-content: center;
  }
  
  .pagination-component {
    justify-content: center;
  }
  
  .table-actions {
    padding: 10px 16px;
    flex-wrap: wrap;
  }
}

.pagination-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.page-size-selector {
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-size-text {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.65);
}

.page-size-select {
  width: 80px;
}

.total-text {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.65);
}

.pagination-component {
  display: flex;
  align-items: center;
}

.column-list {
  max-height: 400px;
  overflow-y: auto;
  padding: 8px 0;
}

.column-item {
  padding: 8px;
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.3s;
}

.column-item:hover {
  background: #fafafa;
}

.column-item :deep(.ant-checkbox-wrapper) {
  width: 100%;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}

.description-cell {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.link-cell {
  color: #1890ff;
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
  max-width: 100%;
  vertical-align: middle;
  line-height: inherit;
}

.link-cell:hover {
  color: #40a9ff;
  text-decoration: underline;
}

.tag-cell {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
  max-width: 100%;
}

.custom-tag {
  margin: 0;
  white-space: nowrap;
}

.th-resize-wrap {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 22px;
  padding-right: 8px;
  box-sizing: border-box;
}

.th-title {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.th-resize-handle {
  position: absolute;
  top: -12px;
  right: -10px;
  bottom: -12px;
  width: 10px;
  cursor: col-resize;
  z-index: 2;
  user-select: none;
  touch-action: none;
}

.th-resize-handle::after {
  content: '';
  position: absolute;
  top: 20%;
  bottom: 20%;
  left: 4px;
  width: 2px;
  border-radius: 1px;
  background: transparent;
  transition: background-color 0.15s;
}

.th-resize-handle:hover::after,
.data-table-content.is-col-resizing .th-resize-handle::after {
  background: #1890ff;
}
</style>

<style>
/* 拖拽列宽时全局禁选，避免选中表头文字 */
body.pharmaid-col-resizing {
  cursor: col-resize !important;
  user-select: none !important;
}

body.pharmaid-col-resizing * {
  cursor: col-resize !important;
  user-select: none !important;
}
</style>


